import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LookupItem {
  id: string;
  lookup_group: string;
  value: string;
  display_name: string;
  description: string | null;
  display_order: number | null;
  is_active: boolean;
  parent_lookup_group: string | null;
  parent_value: string | null;
}

interface LookupGroup {
  name: string;
  items: LookupItem[];
  count: number;
}

export function useLookupsDoc() {
  return useQuery({
    queryKey: ['lookups-docs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lookups')
        .select('id, lookup_group, value, display_name, description, display_order, is_active, parent_lookup_group, parent_value')
        .eq('is_active', true)
        .order('lookup_group', { ascending: true })
        .order('display_order', { ascending: true })
        .order('display_name', { ascending: true });
      
      if (error) throw error;
      
      // Group by lookup_group
      const grouped = (data as LookupItem[]).reduce((acc, item) => {
        if (!acc[item.lookup_group]) {
          acc[item.lookup_group] = [];
        }
        acc[item.lookup_group].push(item);
        return acc;
      }, {} as Record<string, LookupItem[]>);
      
      // Convert to array format
      const groups: LookupGroup[] = Object.entries(grouped).map(([name, items]) => ({
        name,
        items,
        count: items.length,
      }));
      
      return {
        groups,
        totalCount: data?.length || 0,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - static reference data
  });
}

export function useLookupGroup(groupName: string) {
  return useQuery({
    queryKey: ['lookup-group', groupName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lookups')
        .select('id, lookup_group, value, display_name, description, display_order, is_active, parent_lookup_group, parent_value')
        .eq('lookup_group', groupName)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('display_name', { ascending: true });

      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Lookup group not found');
      }
      
      return {
        name: groupName,
        items: data as LookupItem[],
        count: data.length,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - static reference data
    enabled: !!groupName,
  });
}
